import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { baseURL } from "services/API";
import {
	buildAccountingOrgParams,
	useAccountingScope,
} from "scenes/Accounting/useAccountingScope";
import { parseApprovalResponse } from "utilities/approvalResponse";

const accountingBase = `${baseURL}/admin/accounting`;

const accountingErrorMessage = (error, fallback) => {
	const data = error?.response?.data;
	if (!data) return error?.message || fallback;
	if (typeof data === "string") return data;
	if (data.message) return data.message;
	if (data.error) return data.error;
	return fallback;
};

export const useAccounting = () => {
	const token = useSelector((state) => state.token);
	const { requiresOrganizationSelection } = useAccountingScope();
	axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

	const [status, setStatus] = useState(null);
	const [eventTypes, setEventTypes] = useState([]);
	const [isLoadingStatus, setLoadingStatus] = useState(true);
	const [isLoadingEventTypes, setLoadingEventTypes] = useState(true);
	const [actionError, setActionError] = useState(null);
	const [isSubmitting, setSubmitting] = useState(false);
	const [refreshKey, setRefreshKey] = useState(0);

	const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

	const orgParams = useCallback(
		(organizationId) => buildAccountingOrgParams(organizationId, requiresOrganizationSelection),
		[requiresOrganizationSelection]
	);

	const canLoadOrgData = useCallback(
		(organizationId) => !requiresOrganizationSelection || Boolean(organizationId),
		[requiresOrganizationSelection]
	);

	useEffect(() => {
		const fetchStatus = async () => {
			setLoadingStatus(true);
			try {
				const response = await axios.get(`${accountingBase}/status`);
				setStatus(response.data);
			} catch {
				setStatus({ accounting_enabled: false, message: "Could not load accounting status." });
			} finally {
				setLoadingStatus(false);
			}
		};
		if (token) fetchStatus();
	}, [token, refreshKey]);

	useEffect(() => {
		const fetchEventTypes = async () => {
			setLoadingEventTypes(true);
			try {
				const response = await axios.get(`${accountingBase}/event-types`);
				setEventTypes(response.data?.event_types ?? []);
			} catch {
				setEventTypes([]);
			} finally {
				setLoadingEventTypes(false);
			}
		};
		if (token) fetchEventTypes();
	}, [token, refreshKey]);

	const fetchAccounts = useCallback(
		async (organizationId) => {
			if (!canLoadOrgData(organizationId)) {
				return { success: true, accounts: [] };
			}
			try {
				const response = await axios.get(`${accountingBase}/accounts`, {
					params: orgParams(organizationId),
				});
				return {
					success: true,
					accounts: response.data?.accounts ?? [],
				};
			} catch (error) {
				return {
					success: false,
					message: accountingErrorMessage(error, "Failed to load accounts."),
					accounts: [],
				};
			}
		},
		[token, canLoadOrgData, orgParams]
	);

	const createAccount = async (payload) => {
		setSubmitting(true);
		setActionError(null);
		try {
			const response = await axios.post(`${accountingBase}/accounts`, payload);
			refresh();
			return { success: true, data: response.data };
		} catch (error) {
			const message = accountingErrorMessage(error, "Failed to create account.");
			setActionError(message);
			return { success: false, message };
		} finally {
			setSubmitting(false);
		}
	};

	const updateAccount = async (accountId, payload) => {
		setSubmitting(true);
		setActionError(null);
		try {
			const response = await axios.patch(`${accountingBase}/accounts/${accountId}`, payload);
			refresh();
			return { success: true, data: response.data };
		} catch (error) {
			const message = accountingErrorMessage(error, "Failed to update account.");
			setActionError(message);
			return { success: false, message };
		} finally {
			setSubmitting(false);
		}
	};

	const fetchPostingTemplates = useCallback(
		async (organizationId) => {
			if (!canLoadOrgData(organizationId)) {
				return { success: true, templates: [] };
			}
			try {
				const response = await axios.get(`${accountingBase}/posting-templates`, {
					params: orgParams(organizationId),
				});
				return {
					success: true,
					templates: response.data?.templates ?? [],
				};
			} catch (error) {
				return {
					success: false,
					message: accountingErrorMessage(error, "Failed to load posting templates."),
					templates: [],
				};
			}
		},
		[token, canLoadOrgData, orgParams]
	);

	const savePostingTemplate = async (payload) => {
		setSubmitting(true);
		setActionError(null);
		try {
			const response = await axios.post(`${accountingBase}/posting-templates`, payload);
			refresh();
			return { success: true, data: response.data };
		} catch (error) {
			const message = accountingErrorMessage(error, "Failed to save posting template.");
			setActionError(message);
			return { success: false, message };
		} finally {
			setSubmitting(false);
		}
	};

	const fetchPaymentMethodMappings = useCallback(
		async (organizationId, { activeOnly = true } = {}) => {
			if (!canLoadOrgData(organizationId)) {
				return { success: true, mappings: [] };
			}
			try {
				const params = { ...orgParams(organizationId) };
				if (!activeOnly) params.active_only = false;
				const response = await axios.get(`${accountingBase}/payment-method-mappings`, { params });
				return {
					success: true,
					mappings: response.data?.mappings ?? [],
					organizationId: response.data?.organization_id,
				};
			} catch (error) {
				return {
					success: false,
					message: accountingErrorMessage(error, "Failed to load payment method mappings."),
					mappings: [],
				};
			}
		},
		[token, canLoadOrgData, orgParams]
	);

	const savePaymentMethodMapping = async (payload) => {
		setSubmitting(true);
		setActionError(null);
		try {
			const response = await axios.post(`${accountingBase}/payment-method-mappings`, payload);
			refresh();
			return { success: true, data: response.data };
		} catch (error) {
			const message = accountingErrorMessage(error, "Failed to save payment method mapping.");
			setActionError(message);
			return { success: false, message };
		} finally {
			setSubmitting(false);
		}
	};

	const updatePaymentMethodMapping = async (mappingId, payload) => {
		setSubmitting(true);
		setActionError(null);
		try {
			const response = await axios.patch(
				`${accountingBase}/payment-method-mappings/${mappingId}`,
				payload
			);
			refresh();
			return { success: true, data: response.data };
		} catch (error) {
			const message = accountingErrorMessage(error, "Failed to update payment method mapping.");
			setActionError(message);
			return { success: false, message };
		} finally {
			setSubmitting(false);
		}
	};

	const deletePaymentMethodMapping = async (mappingId) => {
		setSubmitting(true);
		setActionError(null);
		try {
			await axios.delete(`${accountingBase}/payment-method-mappings/${mappingId}`);
			refresh();
			return { success: true };
		} catch (error) {
			const message = accountingErrorMessage(error, "Failed to deactivate payment method mapping.");
			setActionError(message);
			return { success: false, message };
		} finally {
			setSubmitting(false);
		}
	};

	const fetchJournalEntries = useCallback(
		async (filters = {}) => {
			if (!canLoadOrgData(filters.organization_id)) {
				return { success: true, journalEntries: [] };
			}
			try {
				const params = { ...orgParams(filters.organization_id) };
				if (filters.event_type) params.event_type = filters.event_type;
				const response = await axios.get(`${accountingBase}/journal-entries`, { params });
				return {
					success: true,
					journalEntries: response.data?.journal_entries ?? [],
				};
			} catch (error) {
				return {
					success: false,
					message: accountingErrorMessage(error, "Failed to load journal entries."),
					journalEntries: [],
				};
			}
		},
		[token, canLoadOrgData, orgParams]
	);

	const createManualJournalEntry = async (payload) => {
		setSubmitting(true);
		setActionError(null);
		try {
			const response = await axios.post(`${accountingBase}/journal-entries/manual`, payload);
			const approval = parseApprovalResponse(response);
			if (approval.pendingApproval) {
				return {
					success: true,
					pendingApproval: true,
					approvalRequestId: approval.approvalRequestId,
					message:
						approval.message ||
						`Submitted for approval (request #${approval.approvalRequestId}). Check Audit & Approvals.`,
				};
			}
			refresh();
			return { success: true, data: response.data };
		} catch (error) {
			const message = accountingErrorMessage(error, "Failed to post manual journal entry.");
			setActionError(message);
			return { success: false, message };
		} finally {
			setSubmitting(false);
		}
	};

	const fetchTrialBalance = useCallback(
		async ({ organization_id, date_from, date_to }) => {
			if (!canLoadOrgData(organization_id)) {
				return { success: false, message: "Select an organisation." };
			}
			try {
				const response = await axios.get(`${accountingBase}/reports/trial-balance`, {
					params: { ...orgParams(organization_id), date_from, date_to },
				});
				return { success: true, data: response.data };
			} catch (error) {
				return {
					success: false,
					message: accountingErrorMessage(error, "Failed to load trial balance."),
				};
			}
		},
		[token, canLoadOrgData, orgParams]
	);

	const fetchGeneralLedger = useCallback(
		async ({ organization_id, account_id, date_from, date_to }) => {
			if (!canLoadOrgData(organization_id)) {
				return { success: false, message: "Select an organisation." };
			}
			try {
				const params = { ...orgParams(organization_id), account_id, date_from };
				if (date_to) params.date_to = date_to;
				const response = await axios.get(`${accountingBase}/reports/general-ledger`, { params });
				return { success: true, data: response.data };
			} catch (error) {
				return {
					success: false,
					message: accountingErrorMessage(error, "Failed to load general ledger."),
				};
			}
		},
		[token, canLoadOrgData, orgParams]
	);

	const fetchProfitAndLoss = useCallback(
		async ({ organization_id, date_from, date_to }) => {
			if (!canLoadOrgData(organization_id)) {
				return { success: false, message: "Select an organisation." };
			}
			try {
				const response = await axios.get(`${accountingBase}/reports/profit-and-loss`, {
					params: { ...orgParams(organization_id), date_from, date_to },
				});
				return { success: true, data: response.data };
			} catch (error) {
				return {
					success: false,
					message: accountingErrorMessage(error, "Failed to load profit and loss."),
				};
			}
		},
		[token, canLoadOrgData, orgParams]
	);

	const fetchEventLogs = useCallback(
		async (filters = {}) => {
			try {
				const params = {};
				if (filters.event_type) params.event_type = filters.event_type;
				const response = await axios.get(`${accountingBase}/event-logs`, { params });
				return {
					success: true,
					logs: response.data?.logs ?? [],
				};
			} catch (error) {
				return {
					success: false,
					message: accountingErrorMessage(error, "Failed to load event logs."),
					logs: [],
				};
			}
		},
		[token]
	);

	const emitEvent = async (event_type, payload) => {
		setSubmitting(true);
		setActionError(null);
		try {
			const response = await axios.post(`${accountingBase}/events/emit`, {
				event_type,
				payload,
			});
			refresh();
			return { success: true, data: response.data };
		} catch (error) {
			const message = accountingErrorMessage(error, "Failed to emit event.");
			setActionError(message);
			return { success: false, message };
		} finally {
			setSubmitting(false);
		}
	};

	return {
		status,
		eventTypes,
		isLoadingStatus,
		isLoadingEventTypes,
		actionError,
		isSubmitting,
		refresh,
		fetchAccounts,
		createAccount,
		updateAccount,
		fetchPostingTemplates,
		savePostingTemplate,
		fetchPaymentMethodMappings,
		savePaymentMethodMapping,
		updatePaymentMethodMapping,
		deletePaymentMethodMapping,
		fetchJournalEntries,
		createManualJournalEntry,
		fetchTrialBalance,
		fetchGeneralLedger,
		fetchProfitAndLoss,
		fetchEventLogs,
		emitEvent,
	};
};
